import type { DateTime } from "luxon";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { type SelfSchedulingDayStructure, SelfSchedulingPreference } from "../../../utils/models";
import type { SelfScheduleCalendarDaysData } from "../SelfSchedulingCalendar";

export type MetricsData = { rdoCount: number; signupShiftsCurrentAmount: number; weekendsCurrentAmount: number };

const getMetrics = (data: Record<string, SelfSchedulingDayStructure>, sundayIsFirstDayOfWeek: boolean): MetricsData => {
	if (!data) return { rdoCount: 0, signupShiftsCurrentAmount: 0, weekendsCurrentAmount: 0 };

	let rdoCount = 0;
	let signupShiftsCurrentAmount = 0;
	let weekendsCurrentAmount = 0;

	Object.values(data).forEach((dayData) => {
		if (dayData.dayPreference === SelfSchedulingPreference.RDO) rdoCount++;

		if (dayData.dayPreference === SelfSchedulingPreference.WANT_TO_WORK) {
			signupShiftsCurrentAmount++;
			if (
				sundayIsFirstDayOfWeek ? dayData.date.weekday % 7 === 5 || dayData.date.weekday % 7 === 6 : dayData.date.weekday % 7 === 6 || dayData.date.weekday === 7
			) {
				weekendsCurrentAmount++;
			}
		}
	});
	return { rdoCount, signupShiftsCurrentAmount, weekendsCurrentAmount };
};

const signUpShiftsTargetAmount = 18;
const weekendsTargetAmount = 4;
const rdoMaxAmount = 6;

/**
 * Custom hook to manage the state of the self-scheduling page.
 */
const useSelfSchedulingState = () => {
	const { t } = useTranslation();
	const metricsRef = useRef<HTMLDivElement>(null);
	const [daysData, setDaysData] = useState<SelfScheduleCalendarDaysData>({});
	const [sundayIsFirstDayOfWeek, setSundayIsFirstDayOfWeek] = useState(false);
	const { showSnackbar } = useSnackbar();
	const currentMetrics = useMemo(() => getMetrics(daysData, sundayIsFirstDayOfWeek), [daysData, sundayIsFirstDayOfWeek]);
	const [unsavedChanges, setUnsavedChanges] = useState(false);
	const [isPublishWarningOpen, setIsPublishWarningOpen] = useState(false);

	const warningMessage = useMemo(() => {
		let message = "";
		if (currentMetrics.signupShiftsCurrentAmount < signUpShiftsTargetAmount) {
			message += t("signup.shifts.not.enough");
		}
		if (currentMetrics.weekendsCurrentAmount < weekendsTargetAmount) {
			if (message) {
				message += " ";
			}
			message += t("signup.weekend.not.enough");
		}
		if (currentMetrics.rdoCount > rdoMaxAmount) {
			if (message) {
				message += " ";
			}
			message += t("too.much.rdo");
		}
		return message;
	}, [currentMetrics, t]);

	const handleSaveClick = useCallback(() => {
		//todo perform actual save logic here
		setUnsavedChanges(false);
		showSnackbar(!warningMessage ? t("preferences.updated") : t("preferences.saved.warning", { warningMessage }), !warningMessage ? "success" : "info");
	}, [warningMessage, showSnackbar, t]);

	const handleOnDayClick = useCallback((d: DateTime, updatedPreference: SelfSchedulingPreference) => {
		setUnsavedChanges(true);
		setDaysData((prevState) => {
			prevState[d.toISO() as string] = {
				date: d,
				dayPreference: updatedPreference,
			};
			return { ...prevState };
		});
	}, []);

	const togglePublishWarning = useCallback(() => {
		setIsPublishWarningOpen((prev) => !prev);
	}, []);

	const handlePerformPublish = useCallback(() => {
		//todo perform actual publish logic here
		showSnackbar("Preferences published successfully!", "success");
		togglePublishWarning();
	}, [showSnackbar, togglePublishWarning]);

	return {
		daysData,
		sundayIsFirstDayOfWeek,
		currentMetrics,
		metricsRef,
		unsavedChanges,
		isPublishWarningOpen,
		handleSaveClick,
		handleOnDayClick,
		togglePublishWarning,
		handlePerformPublish,
		rdoMaxAmount,
		signUpShiftsTargetAmount,
		weekendsTargetAmount,
	};
};

export default useSelfSchedulingState;
